import { client, validateClientProperty } from "./ts-rest"
import { QueryOptions, UseQueryOptions } from "@tanstack/react-query"
import type { PermissionName, KeyName } from '@simulead/database/browser'
import { useTempStore } from "./store"
import { getIsCallCompleted } from "./utils"
import { useState, useCallback, useEffect } from "react"
import { useMatches, useNavigate } from "@tanstack/react-router"
import { update as updateIntercom } from "@intercom/messenger-js-sdk"
import {
  getUserPermissions,
  getAlwaysAvailableCoachingScenarioIds,
} from "@simulead/shared"
import { defaultCoachingScenarioId } from "./variables"
import { useQueryClient } from "@tanstack/react-query"

export function useGetAuthData() {
  return client.getAuthData.useQuery({
    queryKey: [validateClientProperty("getAuthData")],
    staleTime: Infinity,
  })
}

export function useGetCmsData({ enabled = true }: { enabled?: boolean } = {}) {
  return client.getCmsData.useQuery({
    queryKey: [validateClientProperty("getCmsData")],
    staleTime: Infinity,
    enabled,
  })
}

// The live coaching session scenario id: the CoachingScenarioId setting as
// served in the CMS payload, or the long-standing default while the payload is
// loading or the setting is unset. Every "is this the coaching scenario?" check
// in the app reads this rather than a constant.
export function useCoachingScenarioId() {
  const { data: cmsData } = useGetCmsData()
  return cmsData?.body.variables.coachingScenarioId ?? defaultCoachingScenarioId
}

// ENG-1214 org gate, TRI-STATE by design: `undefined` means "org not loaded
// yet" and must gate NOTHING — `user` hydrates asynchronously, so a boolean
// coercion would read every opted-in user as "disabled" during the load
// window (the camera picker would clobber their saved "Record" preference on
// each page load). Only an explicit `false` (org loaded, opted out) disables.
// Consumers that need a stable effect dep coerce with `=== true`, which is
// safe there because those effects also wait on call-connected state, by
// which point auth is settled. Single source of truth for the client gate —
// every surface (picker tiles, recorder belts, presentation legs, admin
// toggle) derives from this hook; the API's upload route enforces the same
// flag authoritatively.
export function useOrgSessionRecordingEnabled(): boolean | undefined {
  const user = useTempStore((s) => s.user)
  if (!user?.org) return undefined
  return Boolean(user.org.enableSessionRecording)
}

// The one shared config for minting recording-upload URLs. The recorders own
// mint failures (bounded retries, terminal org-gate 404, Sentry policy in
// session/chunked-recorder) — a mid-call destructive toast per failed chunk
// mint would only add noise, so the global error toaster is opted out.
export function useGetRecordingUploadUrlMutation() {
  return client.getRecordingUploadUrl.useMutation({
    meta: { skipGlobalErrorToast: true },
  })
}

// True when the current route is under a public /demo/ path
export function useIsDemoRoute() {
  const matches = useMatches()
  return matches.some((match) => String(match.routeId).includes("/demo/"))
}

export function useModules() {
  const { user } = useTempStore()
  const { data: cmsData } = useGetCmsData()

  const orgFromCms = cmsData?.body.orgsFromCms.find(
    (org) => org.orgName === user?.org.name
  )

  const modules: {
    label: string
    permissionName: PermissionName
  }[] = [
    {
      label: "Difficult Conversations",
      permissionName: "Conversations",
    },
  ]

  if (orgFromCms?.enableCoaching) {
    modules.push({
      label: "AI Coaching",
      permissionName: "Coaching",
    })
  }

  if (orgFromCms?.enablePresentations) {
    modules.push({
      label: "Presentations",
      permissionName: "Presentations",
    })
  }

  return modules
}

export function useUserPermissions({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { user } = useTempStore()
  const { data: cmsData } = useGetCmsData({ enabled })

  if (user && cmsData?.body.orgsFromCms) {
    return getUserPermissions({
      orgsFromCms: cmsData.body.orgsFromCms,
      user,
    })
  } else {
    const defaultPermissions = {
      enableConversations: false,
      enablePresentations: false,
      enableCoaching: false,
      personalScenarioCreatorAccess: "allUsers" as const,
      // No org allowlist known yet — the OSC gate fails closed on its own while
      // user/CMS data is still loading (see useOrgScenarioCreatorAccess)
      isOrgScenarioCreatorEmailAllowed: true,
    }

    return defaultPermissions
  }
}

// The element type of cmsData.body.scenarios — used so the resolver accepts the
// same scenario objects the rest of the frontend already works with.
type FrontendScenario = NonNullable<
  ReturnType<typeof useGetCmsData>["data"]
>["body"]["scenarios"][number]

// Resolves which character should voice and represent a scenario for the current
// user. For coaching scenarios (the main coaching session and the post-call
// feedback coach) it returns the user's chosen coach when one is set and present
// in the roster; otherwise it returns the scenario's own character. Non-coaching
// scenarios are always returned unchanged.
export function useResolvedCoachCharacter(scenario: FrontendScenario | undefined) {
  const { user } = useTempStore()
  const { data: cmsData } = useGetCmsData()
  const coachingScenarioId = useCoachingScenarioId()

  if (!scenario) {
    return undefined
  } else {
    const isCoachingScenario = getAlwaysAvailableCoachingScenarioIds({
      coachingScenarioId,
    }).includes(scenario.id)
    const chosenId = user?.chosenCoachCharacterId

    if (!isCoachingScenario || !chosenId) {
      return scenario.character
    } else {
      const chosen = cmsData?.body.coachCharacters?.find(
        (character) => character.id === chosenId,
      )
      return chosen ?? scenario.character
    }
  }
}

// Shared inputs for the scenario-creator gates below. Access is unknown until
// the user (for roles) and CMS org data have loaded — isResolved is false while
// they load — and isCmsError is surfaced so each gate can fail closed rather
// than silently unlocking a restricted org. Demo routes are public pages, so
// they skip the CMS fetch entirely and resolve immediately; what that means for
// access is the individual gate's call.
function useScenarioCreatorGateInputs() {
  const isDemoRoute = useIsDemoRoute()
  const { user } = useTempStore()
  const { data: cmsData, isError: isCmsError } = useGetCmsData({
    enabled: !isDemoRoute,
  })
  const permissions = useUserPermissions({ enabled: !isDemoRoute })
  const isAdmin = user?.roles?.some((role) => role.name === "Admin") ?? false

  const isResolved = isDemoRoute || isCmsError || Boolean(user && cmsData)

  return { isDemoRoute, isAdmin, isCmsError, isResolved, permissions }
}

// Redirects a blocked user back to the scenario chooser, and reports whether the
// page may render. Pages hold rendering on isAllowed — otherwise a deep link
// briefly shows a usable page (with an auto-connecting call) before the redirect
// fires. Shared by both creator guards so they can't drift apart.
function useScenarioCreatorRedirect({
  isResolved,
  isBlocked,
}: {
  isResolved: boolean
  isBlocked: boolean
}) {
  const navigate = useNavigate()

  useEffect(() => {
    if (isBlocked) {
      navigate({ to: "/scenarios/new", replace: true })
    }
  }, [isBlocked, navigate])

  return { isAllowed: isResolved && !isBlocked }
}

// Single evaluation of the Personal Scenario Creator gate, shared by the chooser
// card and the route guard so the two enforcement surfaces can never disagree.
// Demo routes are always allowed — the personal creator has public demo pages.
export function usePersonalScenarioCreatorAccess() {
  const { isDemoRoute, isAdmin, isCmsError, isResolved, permissions } =
    useScenarioCreatorGateInputs()
  const { personalScenarioCreatorAccess } = permissions

  const isBlocked =
    !isDemoRoute &&
    isResolved &&
    (isCmsError ||
      personalScenarioCreatorAccess === "disabled" ||
      (personalScenarioCreatorAccess === "adminsOnly" && !isAdmin))

  return { personalScenarioCreatorAccess, isResolved, isBlocked }
}

// Route guard for the Personal Scenario Creator pages
export function usePersonalScenarioCreatorGuard() {
  return useScenarioCreatorRedirect(usePersonalScenarioCreatorAccess())
}

// Single evaluation of the Organizational Scenario Creator gate, shared by the
// chooser card and the route guard so the two enforcement surfaces can never
// disagree. OSC is admin-only, and an org can narrow it further to a named set
// of users via the CMS allowlist (ENG-1483). Unlike the personal creator, OSC is
// never offered on demo URLs, so demo blocks outright. blockedReason lets the
// chooser card explain the lock without re-deriving the gate.
export function useOrgScenarioCreatorAccess() {
  const { isDemoRoute, isAdmin, isCmsError, isResolved, permissions } =
    useScenarioCreatorGateInputs()
  const { isOrgScenarioCreatorEmailAllowed } = permissions

  const blockedReason = (() => {
    if (isDemoRoute) {
      return "demo" as const
    } else if (!isResolved) {
      return null
    } else if (isCmsError) {
      // Fail closed, but keep it distinct from a real restriction so the copy
      // doesn't tell an admin to contact their administrator over an outage
      return "unavailable" as const
    } else if (!isAdmin) {
      return "notAdmin" as const
    } else if (!isOrgScenarioCreatorEmailAllowed) {
      return "notAllowlisted" as const
    } else {
      return null
    }
  })()

  return { blockedReason, isResolved, isBlocked: blockedReason !== null }
}

// Route guard for the Organizational Scenario Creator page
export function useOrgScenarioCreatorGuard() {
  return useScenarioCreatorRedirect(useOrgScenarioCreatorAccess())
}

export function useCategoriesWithCompletionData(callsUserId?: number) {
  const { data: cmsData } = useGetCmsData()
  const { user } = useTempStore()

  const scenarios = cmsData?.body.scenarios ?? []
  const categories = cmsData?.body.categories ?? []

  const orgsFromCms = cmsData?.body.orgsFromCms

  const { data: callsResponse, isLoading } = client.getCallsForUser.useQuery({
    queryKey: [validateClientProperty("getCallsForUser"), user!.id],
    queryData: {
      params: {
        userId: String(callsUserId ?? user!.id),
      },
    },
  })

  const categoryIdToScenarios = new Map<
    string,
    {
      scenario: (typeof scenarios)[0]
      completed: boolean
    }[]
  >()

  for (const category of categories) {
    const obj: ReturnType<(typeof categoryIdToScenarios)["get"]> = []
    for (const { scenarios_id } of category.scenarios) {
      const scenario = scenarios.find(
        (scenario) => scenario.id === scenarios_id
      )
      if (!scenario) continue

      const callsForScenario =
        callsResponse?.body.calls.filter(
          (item) => item.scenario?.id === scenario.id
        ) ?? []

      let completed = false
      for (const { call } of callsForScenario) {
        if (getIsCallCompleted(call)) {
          completed = true
          break
        }
      }

      obj.push({
        scenario,
        completed,
      })
    }
    categoryIdToScenarios.set(category.id, obj)
  }

  return {
    categoryIdToScenarios,
    categories,
    scenarios,
    isLoading,
  }
}

type ReferralRole = "admin" | "user"

// Global request caps derived from budget thresholds:
// - linkedin_post: ~£500 at £50 reward -> 10 requests
// - community_post: ~£500 at £50 reward -> 10 requests
// - case_study: ~£2500 at £100 reward -> 25 requests
const referralGlobalRequestLimits: Partial<Record<string, number>> = {
  linkedin_post: 10,
  community_post: 10,
  case_study: 25,
}

// Maps action key (from referrals page) + role to the corresponding KeyName for incrementing
const referralActivityKeyMap: Record<string, Record<string, KeyName>> = {
  admin: {
    referral: "ReferralActivityAdminOrgReferralRequested",
    linkedin_post: "ReferralActivityAdminLinkedinRequested",
    case_study: "ReferralActivityAdminCaseStudyRequested",
    community_post: "ReferralActivityAdminCommunityPostRequested",
    quote: "ReferralActivityAdminQuoteRequested",
  },
  user: {
    referral: "ReferralActivityUserOrgReferralRequested",
    linkedin_post: "ReferralActivityUserLinkedinRequested",
    quote: "ReferralActivityUserQuoteRequested",
  },
}

// Hook to fetch referral activity counts and provide an increment function
export function useReferralActivities() {
  const queryClient = useQueryClient()

  // Fetch all referral activity counts
  const { data, isLoading } = client.getReferralActivities.useQuery({
    queryKey: [validateClientProperty("getReferralActivities")],
  })

  const { mutateAsync } = client.incrementReferralActivity.useMutation()

  // Get the count for a specific KeyName, defaulting to 0 if not yet created
  const getCount = useCallback(
    (keyName: string): number => {
      const entry = data?.body?.find((v) => v.key === keyName)
      return entry ? parseInt(entry.value, 10) || 0 : 0
    },
    [data],
  )

  // Increment a referral activity counter by action key and role
  const increment = useCallback(
    async ({ actionKey, role }: { actionKey: string; role: ReferralRole }) => {
      const keyName = referralActivityKeyMap[role]?.[actionKey]
      if (!keyName) return

      await mutateAsync({ body: { key: keyName as Parameters<typeof mutateAsync>[0]["body"]["key"] } })
      // Refetch counts so the UI reflects the new value
      queryClient.invalidateQueries({ queryKey: [validateClientProperty("getReferralActivities")] })
    },
    [mutateAsync, queryClient],
  )

  const getRequestedCountForAction = useCallback(
    (actionKey: string): number => {
      const roles: ReferralRole[] = ["admin", "user"]
      return roles.reduce((total, role) => {
        const requestedKeyName = referralActivityKeyMap[role]?.[actionKey]
        if (!requestedKeyName) return total
        return total + getCount(requestedKeyName)
      }, 0)
    },
    [getCount],
  )

  const getActionRequestLimit = useCallback((actionKey: string): number | undefined => {
    return referralGlobalRequestLimits[actionKey]
  }, [])

  const isActionLimitReached = useCallback(
    (actionKey: string): boolean => {
      const limit = referralGlobalRequestLimits[actionKey]
      if (typeof limit !== "number") return false
      return getRequestedCountForAction(actionKey) >= limit
    },
    [getRequestedCountForAction],
  )

  return {
    activities: data?.body ?? [],
    isLoading,
    getCount,
    increment,
    getRequestedCountForAction,
    getActionRequestLimit,
    isActionLimitReached,
    referralActivityKeyMap,
  }
}

// Fetches outage banner state — polls every 5 minutes so banner appears quickly during incidents
export function useOutageBanner() {
  return client.getOutageBanner.useQuery({
    queryKey: [validateClientProperty("getOutageBanner")],
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}

// Hides the Intercom launcher while `hidden` is true so its "how can we help"
// popup doesn't distract users mid-call in the onboarding flow (ENG-999). The
// launcher is restored when `hidden` becomes false or the calling component
// unmounts. Explicit support entry points (e.g. "Chat with support" buttons)
// keep working while hidden since they call show() directly.
export function useHideIntercomLauncher(hidden: boolean) {
  useEffect(() => {
    updateIntercom({ hide_default_launcher: hidden })
    return () => {
      updateIntercom({ hide_default_launcher: false })
    }
  }, [hidden])
}
